import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus, FolderTree, Sparkles, Copy, Trash2, Search, Lock, GitBranch, Layers,
  Building2, User as UserIcon, Users, Truck, Wand2, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import InfoTooltip from '../components/InfoTooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INDUSTRIES = [
  { value: 'all', label: 'All Industries' },
  { value: 'tax', label: 'Tax' },
  { value: 'accounting', label: 'Accounting' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'legal', label: 'Legal' },
  { value: 'hr', label: 'HR' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'construction', label: 'Construction' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'franchise', label: 'Franchise' },
  { value: 'creative', label: 'Creative' },
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'fitness', label: 'Fitness' },
  { value: 'faith', label: 'Faith-based' },
  { value: 'government', label: 'Government' },
  { value: 'custom', label: 'Other / Custom' },
];

const CLIENT_TYPES = [
  { value: 'all', label: 'All Client Types' },
  { value: 'individual', label: 'Individual' },
  { value: 'small_business', label: 'Small Business' },
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'nonprofit', label: 'Nonprofit' },
  { value: 'custom', label: 'Custom' },
];

const SPACE_FILTERS = [
  { value: 'all', label: 'All Spaces', icon: Layers },
  { value: 'firm', label: 'Organization Files', icon: Building2 },
  { value: 'personal', label: 'My Files', icon: UserIcon },
  { value: 'client', label: 'Client Files', icon: Users },
  { value: 'vendor', label: 'Vendor Files', icon: Truck },
  { value: 'prospect', label: 'Prospect Files', icon: Sparkles },
];

const INDUSTRY_LABEL = Object.fromEntries(INDUSTRIES.map(i => [i.value, i.label]));
const CLIENT_LABEL = Object.fromEntries(CLIENT_TYPES.map(c => [c.value, c.label]));
const SPACE_LABEL = Object.fromEntries(SPACE_FILTERS.map(s => [s.value, s.label]));

const TIER_BADGE = {
  starter: { label: 'Locked Starter', icon: Lock, cls: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300' },
  customized_starter: { label: 'Customized Starter', icon: Wand2, cls: 'bg-violet-100 text-violet-700 border-violet-300 dark:bg-violet-900/30 dark:text-violet-300' },
  custom: { label: 'Custom', icon: Pencil, cls: 'bg-sky-100 text-sky-700 border-sky-300 dark:bg-sky-900/30 dark:text-sky-300' },
};

const TAB_DESCRIPTIONS = {
  general: 'Universal starting points that work for any business or solo user.',
  specialty: 'Industry-specific templates pre-built for tax, legal, healthcare, construction, real estate and more.',
  my: 'Templates you have created or customized for this workspace.',
};

export default function FolderTemplates() {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [industry, setIndustry] = useState('all');
  const [clientType, setClientType] = useState('all');
  const [spaceFilter, setSpaceFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('general');
  const [applying, setApplying] = useState(null);
  const [customizing, setCustomizing] = useState(null);

  const fetchTemplates = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ workspace_id: wsId, include_presets: 'true' });
      if (industry !== 'all') qs.append('industry', industry);
      if (clientType !== 'all') qs.append('client_type', clientType);
      if (spaceFilter !== 'all') qs.append('space', spaceFilter);
      const res = await apiFetch(`${API_URL}/api/folder-templates?${qs.toString()}`);
      if (res.ok) setTemplates(await res.json());
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [wsId, industry, clientType, spaceFilter]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // Filter + bucket by tab
  const filteredByTab = useMemo(() => {
    const bySearch = (t) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (t.name || '').toLowerCase().includes(q)
        || (t.description || '').toLowerCase().includes(q);
    };
    const out = { general: [], specialty: [], my: [] };
    templates.filter(bySearch).forEach(t => {
      const tier = t.tier || (t.is_system ? 'starter' : 'custom');
      if (tier === 'starter') {
        // System preset: bucket by its preset category (general | specialty)
        if (t.category === 'specialty') out.specialty.push(t);
        else out.general.push(t);
      } else {
        // Anything the user owns (customized starter OR custom) lives in My Templates
        out.my.push(t);
      }
    });
    return out;
  }, [templates, search]);

  const tabList = filteredByTab[activeTab] || [];

  const handleDuplicate = async (tpl) => {
    try {
      const res = await apiFetch(`${API_URL}/api/folder-templates/${tpl.template_id}/duplicate?workspace_id=${wsId}`, { method: 'POST' });
      if (res.ok) {
        const dup = await res.json();
        toast.success(`Duplicated as "${dup.name}"`);
        fetchTemplates();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to duplicate');
      }
    } catch { toast.error('Failed to duplicate'); }
  };

  const handleCustomize = async (tpl) => {
    setCustomizing(tpl.template_id);
    try {
      const res = await apiFetch(`${API_URL}/api/folder-templates/${tpl.template_id}/customize?workspace_id=${wsId}`, { method: 'POST' });
      if (res.ok) {
        const customized = await res.json();
        toast.success(`Editable copy ready — opening editor…`);
        await fetchTemplates();
        navigate(`/folder-templates/${customized.template_id}/edit`);
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to customize');
      }
    } catch { toast.error('Failed to customize'); }
    finally { setCustomizing(null); }
  };

  const handleApply = async (tpl) => {
    setApplying(tpl.template_id);
    try {
      const res = await apiFetch(`${API_URL}/api/folder-templates/${tpl.template_id}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: wsId }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Created ${data.created_count} folder${data.created_count === 1 ? '' : 's'}`);
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to apply');
      }
    } catch { toast.error('Failed to apply'); }
    finally { setApplying(null); }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"? This cannot be undone.`)) return;
    try {
      const res = await apiFetch(`${API_URL}/api/folder-templates/${tpl.template_id}?workspace_id=${wsId}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Deleted'); fetchTemplates(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Failed to delete'); }
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6" data-testid="folder-templates-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <FolderTree className="w-7 h-7 text-primary" /> Folder Templates
            <InfoTooltip title="Folder Templates" testId="info-folder-templates" side="bottom">
              <p>Reusable folder structures — apply them once and a full tree is created inside any of your <strong>spaces</strong> (Organization, My Files, Clients, Vendors or Prospects).</p>
              <p>Three customization tiers:</p>
              <ul className="list-disc pl-4 space-y-1">
                <li><strong>Locked Starter</strong> — 99+ system presets you can apply as-is.</li>
                <li><strong>Customized Starter</strong> — your editable copy of a starter (auto-created when you click "Customize").</li>
                <li><strong>Custom</strong> — built from scratch for your firm.</li>
              </ul>
            </InfoTooltip>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Save your folder structures once, apply them anywhere — clients, projects, engagements or yearly rollovers.
          </p>
        </div>
        <Button onClick={() => navigate('/folder-templates/new')} data-testid="new-template-btn"
          className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Plus className="w-4 h-4 mr-2" /> New Template
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="relative sm:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search templates…"
                className="pl-10"
                data-testid="template-search"
              />
            </div>
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger data-testid="filter-industry"><SelectValue /></SelectTrigger>
              <SelectContent>
                {INDUSTRIES.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={clientType} onValueChange={setClientType}>
              <SelectTrigger data-testid="filter-client-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CLIENT_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">
              Space
              <InfoTooltip title="What does Space mean?" testId="info-space-filter" side="bottom">
                <p>A <strong>space</strong> determines <em>where</em> a template's folders are created.</p>
                <p>Filter by space to see only templates designed for, e.g., client folders or your own private files.</p>
              </InfoTooltip>
            </span>
            {SPACE_FILTERS.map(s => {
              const Icon = s.icon;
              const isActive = spaceFilter === s.value;
              return (
                <button
                  key={s.value}
                  onClick={() => setSpaceFilter(s.value)}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs border transition-all',
                    isActive
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card hover:bg-muted border-border text-muted-foreground'
                  )}
                  data-testid={`space-filter-${s.value}`}
                >
                  <Icon className="w-3 h-3" /> {s.label}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center gap-3 flex-wrap">
          <TabsList data-testid="template-tabs">
            <TabsTrigger value="general" data-testid="tab-general">
              General <Badge variant="outline" className="ml-2 text-[10px]">{filteredByTab.general.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="specialty" data-testid="tab-specialty">
              Specialty <Badge variant="outline" className="ml-2 text-[10px]">{filteredByTab.specialty.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="my" data-testid="tab-my">
              My Templates <Badge variant="outline" className="ml-2 text-[10px]">{filteredByTab.my.length}</Badge>
            </TabsTrigger>
          </TabsList>
          <p className="text-xs text-muted-foreground italic">{TAB_DESCRIPTIONS[activeTab]}</p>
        </div>
      </Tabs>

      {loading ? (
        <div className="text-center text-muted-foreground py-12">Loading…</div>
      ) : tabList.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Layers className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
            <p className="text-muted-foreground">No templates in this tab match your filters.</p>
            {activeTab === 'my' && (
              <Button onClick={() => navigate('/folder-templates/new')} className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white">
                <Plus className="w-4 h-4 mr-1.5" /> Create your first template
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tabList.map(tpl => {
            const tier = tpl.tier || (tpl.is_system ? 'starter' : 'custom');
            const tierMeta = TIER_BADGE[tier];
            const TierIcon = tierMeta?.icon || Layers;
            return (
              <Card key={tpl.template_id} className={cn(
                'transition-all hover:shadow-md group',
                tier === 'starter' && 'border-amber-300/60 bg-amber-50/20 dark:bg-amber-950/10',
                tier === 'customized_starter' && 'border-violet-300/60 bg-violet-50/20 dark:bg-violet-950/10',
              )} data-testid={`template-card-${tpl.template_id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <FolderTree className="w-4 h-4" style={{ color: tpl.color || '#3b82f6' }} />
                        <h3 className="font-semibold text-base truncate">{tpl.name}</h3>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 min-h-[2rem]">{tpl.description || 'No description.'}</p>
                    </div>
                    {tierMeta && (
                      <Badge variant="outline" className={cn('text-[9px] flex-shrink-0', tierMeta.cls)} data-testid={`tier-badge-${tier}`}>
                        <TierIcon className="w-2.5 h-2.5 mr-0.5" /> {tierMeta.label}
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 flex-wrap">
                    {tpl.space && (
                      <Badge variant="secondary" className="text-[10px]" data-testid={`space-badge-${tpl.space}`}>
                        {SPACE_LABEL[tpl.space] || tpl.space}
                      </Badge>
                    )}
                    <Badge variant="secondary" className="text-[10px]">{INDUSTRY_LABEL[tpl.industry] || tpl.industry}</Badge>
                    <Badge variant="outline" className="text-[10px]">{CLIENT_LABEL[tpl.client_type] || tpl.client_type}</Badge>
                    <Badge variant="outline" className="text-[10px] flex items-center gap-1">
                      <Layers className="w-2.5 h-2.5" />{tpl.node_count || tpl.nodes?.length || 0} folders
                    </Badge>
                    {tpl.version > 1 && (
                      <Badge className="text-[10px] bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 flex items-center gap-1">
                        <GitBranch className="w-2.5 h-2.5" /> v{tpl.version}
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-2 pt-2 border-t flex-wrap">
                    <Button size="sm" onClick={() => handleApply(tpl)}
                      disabled={applying === tpl.template_id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`apply-template-${tpl.template_id}`}>
                      <Sparkles className="w-3 h-3 mr-1.5" />
                      {applying === tpl.template_id ? 'Applying…' : 'Apply'}
                    </Button>
                    {tier === 'starter' ? (
                      <Button size="sm" variant="outline" onClick={() => handleCustomize(tpl)}
                        disabled={customizing === tpl.template_id}
                        data-testid={`customize-template-${tpl.template_id}`}>
                        <Wand2 className="w-3 h-3 mr-1.5" />
                        {customizing === tpl.template_id ? 'Preparing…' : 'Customize'}
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => handleDuplicate(tpl)}
                        data-testid={`duplicate-template-${tpl.template_id}`}>
                        <Copy className="w-3 h-3 mr-1.5" /> Duplicate
                      </Button>
                    )}
                    {tier !== 'starter' && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/folder-templates/${tpl.template_id}/edit`)}
                          className="ml-auto"
                          data-testid={`edit-template-${tpl.template_id}`}>
                          Edit
                        </Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(tpl)}
                          data-testid={`delete-template-${tpl.template_id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
